import { supabase } from '../lib/supabase'
import type { WriteResult } from './projectWrites'

/**
 * WHERE THE DAY HAPPENS — 28 Oct 2026.
 *
 * A call sheet used to hold one address and one Google Maps link. Tom:
 * *"in one shoot day we might have multiple locations (like this one) how can
 * we do this and make it clear and easy?"*
 *
 * It was not a hypothetical. Two of the four call sheets in the database had
 * already been typed as a hand-numbered list inside the single address box —
 *
 *     1. Mövenpick Resort Al Marjan Island, …
 *     2. Marjan HQ, Al Marjan Island …
 *
 * — and because there was only ever one map link, the second address had no
 * way to be opened. The numbering was doing the work a data structure should
 * have been doing, and failing at the one thing that matters on the day:
 * getting a driver to the right gate.
 *
 * So a location is now a thing with a NAME. The name is the point: it is what
 * the schedule's Where column says, what the crew sheet lists at the top, and
 * what ties the two together without anyone retyping an address.
 *
 * **Stored as one jsonb column, not a child table.** `set_shoot_day_locations`
 * replaces the whole list in a single UPDATE. A child table would have meant a
 * delete-then-insert, and a function body containing a DELETE cannot be
 * created from an agent session — the MCP destructive-statement gate stops it
 * and the call times out after three minutes. That cost a morning on the news
 * digest; it is not worth paying twice for a list that is never queried across
 * call sheets.
 *
 * **`location_address` and `location_map_url` are still written**, as a
 * flattened mirror of the list. The crew and client call sheet at
 * callsheet.portalproductions.me is a SEPARATE Magic Patterns app, and the PDF
 * reads the same two fields. Keeping the mirror in step means every address
 * reaches the crew today, rather than after a second app has been rebuilt.
 * The mirror is derived, never edited: `locations` is the truth.
 */

export interface CallSheetLocation {
  /** Short, human, and used as the key: "Red Rock", "Marjan HQ". */
  name: string
  address: string
  mapUrl: string
}

export function emptyLocation(): CallSheetLocation {
  return { name: '', address: '', mapUrl: '' }
}

/** A location worth saving — anything with a name or an address in it. */
export function isRealLocation(location: CallSheetLocation): boolean {
  return Boolean(location.name.trim() || location.address.trim())
}

/** '' for anything that is not a string, so a malformed payload cannot throw. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * The name to offer for an address nobody has named yet.
 *
 * Real addresses put the place first and the geography after it, separated by
 * a comma or a dash — "Mövenpick Resort Al Marjan Island, Al Marjan Is Blvd",
 * "Red Rock - Jebel Jais - Wadi Shehah". Whichever separator comes first ends
 * the name. It is a starting point, not a guess to be trusted: it lands in an
 * editable field.
 */
function nameFromAddress(line: string): string {
  const cleaned = line.replace(/^\s*\d+\s*[.)]\s*/, '').trim()
  const comma = cleaned.indexOf(',')
  const dash = cleaned.indexOf(' - ')
  const cuts = [comma, dash].filter((index) => index > 0)
  const cut = cuts.length ? Math.min(...cuts) : -1
  const name = cut > 0 ? cleaned.slice(0, cut) : cleaned
  return name.trim().slice(0, 60)
}

/**
 * Turn a legacy address box into a list.
 *
 * Splits on newlines, drops any "1. " / "2) " the typist added, and names each
 * line from its own first clause. The one stored map link goes to the first
 * location, because that is the one it was almost certainly for.
 */
export function parseLegacyAddress(address: string, mapUrl: string): CallSheetLocation[] {
  const lines = address
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*\d+\s*[.)]\s*/, '').trim())
    .filter(Boolean)

  if (lines.length === 0) return []

  return lines.map((line, index) => ({
    name: nameFromAddress(line),
    address: line,
    mapUrl: index === 0 ? mapUrl.trim() : '',
  }))
}

/**
 * The locations on a call sheet payload.
 *
 * Takes the payload loosely on purpose — `locations` was added to
 * `get_call_sheet` on 28 Oct and is not on the `CallSheetPayload` interface,
 * so everything here is narrowed from `unknown` rather than trusted.
 *
 * Falls back to parsing the old single address box when the list is empty, so
 * a sheet written before today opens with its addresses already separated
 * rather than as one block of text to be unpicked by hand. Nothing is written
 * back until the sheet is saved — same shape as the Important Instructions /
 * Client Notes merge of 8 Sep.
 */
export function readLocations(payload: Record<string, unknown>): CallSheetLocation[] {
  const raw = Array.isArray(payload.locations) ? payload.locations : []

  const stored = raw
    .map((entry) => {
      const row = (entry ?? {}) as Record<string, unknown>
      return {
        name: text(row.name),
        address: text(row.address),
        mapUrl: text(row.mapUrl),
      }
    })
    .filter(isRealLocation)

  if (stored.length) return stored

  return parseLegacyAddress(text(payload.locationAddress), text(payload.locationMapUrl))
}

/**
 * The list as one block of text, for the crew app and the PDF.
 *
 * Must match what `set_shoot_day_locations` writes server side, so the two
 * cannot disagree about what the crew are reading.
 */
export function flattenAddress(locations: CallSheetLocation[]): string {
  return locations
    .filter(isRealLocation)
    .map((location) =>
      location.name.trim() ? `${location.name.trim()} — ${location.address.trim()}` : location.address.trim(),
    )
    .join('\n')
}

/** The first link anyone actually provided, for the single legacy map field. */
export function firstMapUrl(locations: CallSheetLocation[]): string {
  return locations.find((location) => location.mapUrl.trim())?.mapUrl.trim() ?? ''
}

/** The names the schedule offers as suggestions, in order, without blanks. */
export function locationNames(locations: CallSheetLocation[]): string[] {
  const names = locations.map((location) => location.name.trim()).filter(Boolean)
  return names.filter((name, index) => names.indexOf(name) === index)
}

/** Replaces the whole list in one call, like every other part of a call sheet. */
export async function saveCallSheetLocations(
  callSheetId: string,
  locations: CallSheetLocation[],
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const { error } = await supabase.rpc('set_shoot_day_locations', {
    p_shoot_day_id: callSheetId,
    p_items: locations.filter(isRealLocation).map((location) => ({
      name: location.name.trim(),
      address: location.address.trim(),
      mapUrl: location.mapUrl.trim(),
    })),
  })

  if (error) {
    const detail = `set_shoot_day_locations: ${error.message}${error.code ? ` (${error.code})` : ''}`
    console.error(`[supabase] rpc refused — ${detail}`)
    return { ok: false, error: detail }
  }
  return { ok: true }
}

